import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { MATERIAL_ICON_DOWNLOAD, MATERIAL_ICON_VIDEO } from '../../../shared/common/Button/Icon';
import { FilterBar } from '../../../shared/common/FilterBar';
import Pagination from '../../../shared/common/Pagination';
import ProductList from '../../../shared/common/ProductList';
import NoElementsFound, { NO_ELEMENTS_CAMPAIGNS } from '../../../shared/components/NoElementsFound';
import VideoPlayerPopup from '../../../shared/components/VideoPlayerPopup';
import ContentBaseLayout from '../../libx/shared/ContentBaseLayout';
import { Button, ButtonSize, ButtonTheme } from '../../../shared/common/Button';
import { useParams } from 'react-router';
import { getLoanFormat } from '../../../shared/utils/getLoanFormat';
import { ProductSorting } from '../../../../models/Product';
import {
    invalidateYourCampaignProducts,
    useYourCampaignProducts
} from '../../../api/marketing/your-campaigns/useYourCampaignProducts';
import { useUpdateCampaignSettings } from '../../../api/campaign-products/useUpdateCampaignSettings';
import { ResponsiveFilterBar } from '../../../shared/filter/ResponsiveFilterBar';
import { fetchYourCampaignsAggregationsQuickSearch } from '../../../api/marketing/your-campaigns/fetchYourCampaignsAggregationsQuickSearch';
import { QueryKeyBuilder } from '../../../shared/filter/useAggregationsQuery';
import { FetchAggregations } from '../../../api/listContent/fetchAggregations';
import { fetchYourCampaignsAggregations } from '../../../api/marketing/your-campaigns/fetchYourCampaignsAggregations';
import { FetchAggregationsQuickSearch } from '../../../api/listContent/fetchAggregationsQuickSearch';
import { CampaignListItem } from './CampaignListItem';
import { downloadCampaignGuide } from '../../../api/marketing/your-campaigns/downloadCampaignGuide';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useAddToList } from '../../../api/selection-list/useAddSingleToList';
import { useAddToCart } from '../../../api/shopping-cart/useAddToCart';
import { SingleAddOptions } from '../../../../models/ListContentCheckOptions';
import { ListActionTarget } from '../../../../models/ListActionTarget';
import { useListContentChecks } from '../../../shared/common/useListContentChecks';
import { CheckBeforeAddingModal } from '../../../shared/common/CheckBeforeAddingModal';
import { useAgeCategorySearchParam } from '../../../shared/utils/useAgeCategorySearchParam';
import { UnavailableProductsModal } from '../../../shared/common/UnavailableProductsModal';
import { DISPLAY_TYPE_LIST } from '../../../shared/common/displayType/types';
import { useDisplayFilterSearchParam } from '../../../shared/utils/useDisplayFilterSearchParam';
import { LOANFORMAT_ALL_OBJECT } from '../../../shared/loanFormat/constants';
import { MarketingCampaignProductOut } from '../../../store/api';
import { usePaginationAndSortParams } from '../../../shared/hooks/usePaginationAndSortParams';
import { useProductFilters } from '../../../shared/filter/useProductFilters';
import { useSearchParams } from 'react-router-dom';
import { LANGUAGE_DE_DE } from '../../../../i18n';
import { useDefaultVisibleFilterGroups } from '../../../shared/filter/defaultVisibleFilterGroups';

const StFilterBar = styled(FilterBar)`
    margin: 10px 0 40px 0;
`;

const StButtonLine = styled.div.attrs({ 'data-displayname': 'HeaderButtonLine' })`
    margin-bottom: 62px;

    * ~ * {
        margin-left: 8px;
    }
`;

export const YourCampaignsPage: FC = () => {
    const {
        t,
        i18n: { language }
    } = useTranslation();
    const [searchParams] = useSearchParams();
    const [showIntroVideo, setShowIntroVideo] = useState(false);
    const [urlParamHandled, setUrlParamHandled] = useState(false);
    const { loanFormat: loanFormatPathParam } = useParams<'loanFormat'>();
    const loanFormat = getLoanFormat(loanFormatPathParam);

    const { displayFilter } = useDisplayFilterSearchParam();
    const { page, sortBy, pageSize } = usePaginationAndSortParams({
        defaultPageSize: 54,
        defaultSortBy: ProductSorting.RELEASE_DATE_NEW_TO_OLD
    });

    const { productFilter } = useProductFilters();
    const { value: ageCategory } = useAgeCategorySearchParam();

    const { products, isLoading: isCampaignProductsLoading } = useYourCampaignProducts({
        filterMap: productFilter,
        pagination: { sortBy, page, pageSize },
        ageCategory,
        loanFormat: loanFormat.key === LOANFORMAT_ALL_OBJECT.key ? undefined : loanFormat.key!
    });

    const { mutate: updateCampaignSetting } = useUpdateCampaignSettings();

    const totalProducts = products.count;
    const campaignProducts = products.list;

    const defaultVisibleFilterGroups = useDefaultVisibleFilterGroups(ageCategory);

    //region FILTERBAR_V2
    const visibleFilterGroups = useMemo(
        () => [...defaultVisibleFilterGroups, 'campaignStatus'],
        [defaultVisibleFilterGroups]
    );
    const aggregationsQueryKeyBuilder = useCallback<QueryKeyBuilder>(
        ({ filters, expandedFilterGroups }) => [
            'byGenrePage',
            loanFormat.requestKey,
            'aggregation',
            ageCategory,
            filters,
            expandedFilterGroups
        ],
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregations = useCallback<FetchAggregations>(
        options =>
            fetchYourCampaignsAggregations({
                ...options,
                loanFormat: loanFormat.requestKey!,
                ageCategory
            }),
        [ageCategory, loanFormat.requestKey]
    );
    const fetchAggregationsQuickSearchCallback = useCallback<FetchAggregationsQuickSearch>(
        options =>
            fetchYourCampaignsAggregationsQuickSearch({ ...options, loanFormat: loanFormat.requestKey!, ageCategory }),
        [ageCategory, loanFormat.requestKey]
    );
    //endregion

    useEffect(() => {
        if (searchParams.has('intro') && !urlParamHandled) {
            setShowIntroVideo(true);
            setUrlParamHandled(true);
        }
    }, [searchParams, urlParamHandled]);

    const loading = isCampaignProductsLoading;

    const { addToList } = useAddToList();
    const { addToCart } = useAddToCart({
        callback: () => {}
    }); // note: we don't have to invalidate the products call, since we don't show a "items in cart" icon on this page

    const onAction = useCallback(
        (performChecks: boolean, options: SingleAddOptions) => {
            if (options.target === ListActionTarget.List) {
                return addToList(options.options, options.listId, performChecks);
            } else {
                return addToCart(options.options, performChecks);
            }
        },
        [addToCart, addToList]
    );

    const { changePrice, getCheckBeforeAddingModalProps, getUnavailableProductsModalProps } =
        useListContentChecks<SingleAddOptions>({ onAction });

    function handleDownloadCampaignGuide() {
        downloadCampaignGuide();
    }

    function updateProductCampaignSettings(startDate, endDate, campaignTagLine, productId) {
        updateCampaignSetting(
            {
                campaignProductId: productId,
                settings: {
                    startDate,
                    endDate,
                    campaignTagLine
                }
            },
            {
                onSuccess: () => {
                    invalidateYourCampaignProducts().then();
                }
            }
        );
    }

    return (
        <>
            <ContentBaseLayout
                topComponent={
                    <>
                        {language !== LANGUAGE_DE_DE ? (
                            <StButtonLine>
                                <Button
                                    onClick={() => setShowIntroVideo(true)}
                                    text={t('marketing.yourCampaigns.showIntroVideo')}
                                    size={ButtonSize.LARGE}
                                    theme={ButtonTheme.NEUTRAL}
                                    icon={MATERIAL_ICON_VIDEO}
                                />
                                <Button
                                    onClick={handleDownloadCampaignGuide}
                                    text={t('marketing.yourCampaigns.downloadCampaignGuide')}
                                    icon={MATERIAL_ICON_DOWNLOAD}
                                    size={ButtonSize.LARGE}
                                    theme={ButtonTheme.NEUTRAL}
                                />
                            </StButtonLine>
                        ) : null}
                        {campaignProducts.length > 0 ? (
                            <StFilterBar
                                hideSelectButtons={true}
                                hideGroupBy={true}
                                hideDisplayType={true}
                                totalProducts={totalProducts}
                                pageSize={pageSize}
                                sortBy={sortBy}
                            />
                        ) : null}
                    </>
                }
                sidebarComponent={
                    displayFilter ? (
                        <ResponsiveFilterBar
                            visibleFilterGroups={visibleFilterGroups}
                            buildQueryKey={aggregationsQueryKeyBuilder}
                            fetchAggregations={fetchAggregations}
                            fetchAggregationsQuickSearch={fetchAggregationsQuickSearchCallback}
                        />
                    ) : null
                }
                contentComponent={
                    <div className={classNames({ 'min-h-screen': isCampaignProductsLoading }, 'ca-main')}>
                        {campaignProducts.length < 1 && !loading ? (
                            <NoElementsFound type={NO_ELEMENTS_CAMPAIGNS} />
                        ) : (
                            <div className="ca-main-inner">
                                <ProductList
                                    products={campaignProducts}
                                    displayType={DISPLAY_TYPE_LIST}
                                    onAddPrice={changePrice}
                                    isProductSelectable={false}
                                >
                                    {({ key, isListView, ...value }) => (
                                        <CampaignListItem
                                            {...value}
                                            product={value.product as MarketingCampaignProductOut}
                                            key={key}
                                            updateProductCampaignSettings={updateProductCampaignSettings}
                                        />
                                    )}
                                </ProductList>
                                <Pagination totalItems={totalProducts} pageSize={pageSize} />
                            </div>
                        )}
                    </div>
                }
                modalComponent={
                    <VideoPlayerPopup
                        showVideo={showIntroVideo && language !== LANGUAGE_DE_DE}
                        hideVideo={() => setShowIntroVideo(false)}
                        vimeoId={354627777}
                    />
                }
                documentTitle={t('marketing.yourCampaigns.yourCampaigns')}
                loading={loading}
            />
            <CheckBeforeAddingModal {...getCheckBeforeAddingModalProps()} />
            <UnavailableProductsModal {...getUnavailableProductsModalProps()} />
        </>
    );
};
